import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { API } from '../App';

/**
 * GoogleAuthCallback - Handles the OAuth callback from Google
 * Supports both:
 * 1. Emergent Auth flow (session_id in hash)
 * 2. Custom OAuth flow (code in query params)
 */
const GoogleAuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processGoogleAuth = async () => {
      try {
        // Check for custom OAuth code in query params
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');

        if (errorParam) {
          setError(`Google authentication failed: ${errorParam}`);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Check for Emergent Auth session_id in hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        let requestBody = {};
        
        if (code) {
          // Custom OAuth flow
          requestBody = {
            code: code,
            state: state,
            redirect_uri: `${window.location.origin}/auth/google/callback`
          };
        } else if (sessionIdMatch) {
          // Emergent Auth flow
          requestBody = {
            session_id: sessionIdMatch[1]
          };
        } else {
          setError('No authentication data found. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Send to backend for validation
        const response = await axios.post(`${API}/auth/google/callback`, requestBody);

        if (response.data.success) {
          const { access_token, user } = response.data;

          // Store auth data
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify(user));

          // Store clinic_id if applicable
          if (user.clinic_id) {
            localStorage.setItem('clinic_id', user.clinic_id);
          }

          // Set user in app state
          setUser(user);

          // Mark as just authenticated (for protected route delay skip)
          sessionStorage.setItem('just_authenticated', 'true');

          toast.success('Successfully signed in with Google!');

          // Redirect based on role
          const superAdminRoles = ['super_admin', 'super_owner', 'platform_admin', 'support_admin', 'content_admin'];
          let redirectPath = '/admin';
          if (superAdminRoles.includes(user.role)) {
            redirectPath = '/super-admin';
          } else if (user.role === 'patient') {
            redirectPath = '/patient';
          } else if (user.role === 'clinic_admin') {
            redirectPath = '/admin';
          }
          navigate(redirectPath, { replace: true });
        }
      } catch (error) {
        console.error('Google auth error:', error);
        const errorMessage = error.response?.data?.detail || 'Google authentication failed';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // Redirect to login after showing error
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processGoogleAuth();
  }, [navigate, setUser, location]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Signing in with Google...</h2>
        <p className="text-gray-600">Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
